import { gql, TypedDocumentNode } from '@apollo/client';
import { IDBAsset } from '@netfront/common-library';
import { Button, SkeletonLoader } from '@netfront/ui-library';
import { useEffect, useMemo, useState } from 'react';

import { Card, Container, LessonCards, Page, StudentEvaluationBanner } from '@/components';
import { CONSTANTS } from '@/constants';
import { ISearchAssetsQueryGraphQLResponse } from '@/graphql';
import { useGetLessons } from '@/hooks';
import { getCompletedLessonsCount } from '@/utils';
import { useLazyQuery } from '@netfront/apollo-providers';
import { useGetTailoredFeedback } from '@/hooks/useGetTailoredFeedback';

export const StudentDashboard = () => {
  const { lessons, isLoading: isLessonsLoading = false } = useGetLessons();
  const { feedbackSteps } = useGetTailoredFeedback();
  
  const [documents, setDocuments] = useState<IDBAsset[]>([]);

  const lessonsExludingBackstory = lessons?.filter(({ id }) => id !== CONSTANTS.USERFLOW.CARTOONS.STEPS.BACKSTORY.ID);

  const completedLessonsCount = useMemo(() => getCompletedLessonsCount(lessonsExludingBackstory), [lessonsExludingBackstory]);


  const { handleQuery: getAssets, isLoading: isLoadingAssets } = useLazyQuery<ISearchAssetsQueryGraphQLResponse>({
    apiName: 'ekardo',
    query: gql`
      query {
        asset {
          search(projectId: "parenting-and-teens", types: [DOCUMENT]) {
            edges {
              node {
                assetId
                presignedUrl
                title
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<ISearchAssetsQueryGraphQLResponse>,
      onCompleted: ({ asset }) => setDocuments(asset.search.edges.map(({ node }) => node)),
  });

  useEffect(() => {
    getAssets({});
  }, []);

  const standardDrinkChart = documents.find(({ assetId }) => assetId === CONSTANTS.ASSETS.STANDARD_DRINK_CHART.ID);
  const childDietary = documents.find(({ assetId }) => assetId === CONSTANTS.ASSETS.DIETARY_TEENAGERS.ID);
  const characterPoster = documents.find(({ assetId }) => assetId === CONSTANTS.ASSETS.CHARACTERS_POSTER.ID);

  
  return (
    <Page
      hasPrivateLayout
      meta={{
        seoDescription: 'View and complete your lessons',
        seoTitle: 'Student lessons',
      }}
      title="Student lessons"
    >
      <div className="bg-primary bg-banner-graphic py-8">
        <Container>
          <div className="flex flex-col md:flex-row md:items-center">
            <div className="flex items-center flex-1">
              <img className="mr-4" src="/images/lesson-icon.svg" alt="Cartoon of a slide projector with abstract content." />
              <div>
                <span className="h5 block text-uppercase">Student</span>
                <h1 className="h2 text-uppercase">Lessons</h1>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <section className="bg-white py-10">
        <Container>
          <StudentEvaluationBanner />

          <Card>
            <h2 className="h4 mb-4 text-uppercase">Introduction to Health4Life</h2>
            <p className="mb-4">
              This program aims to improve your skills around 'The Big 6' health behaviours. These health behaviours are:
            </p>
              <ul className="mb-4 list-style-none grid grid-cols-3 md:grid-cols-6 gap-4 p-0">
                {[
                  { label: 'Sleep', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.SLEEP.ICON_URL },
                  { label: 'Screen time', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.SCREEN_TIME.ICON_URL },
                  { label: 'Healthy eating', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.HEALTHY_EATING.ICON_URL },
                  { label: 'Physical activity', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.PHYSICAL_ACTIVITY.ICON_URL },
                  { label: 'Smoking or vaping', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.SMOKING.ICON_URL },
                  { label: 'Alcohol use', icon: CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.ALCOHOL.ICON_URL },
                ].map(({ label, icon }) => (
                  <li key={label} className="flex flex-col items-center gap-2 m-0">
                    <img src={icon} alt="" aria-hidden="true" className="w-12 h-12" />
                    <span className="text-sm text-center">{label}</span>
                  </li>
                ))}
              </ul>
            <p className="mb-4">
              You'll work through six cartoon lessons about the Big 6 health behaviours. You can find short lesson summaries, activities and other helpful resources here in the student centre.
            </p>
            <p>
              Some parts of these lessons may talk about things your age group has not yet come across. It is designed to help you feel more ready to handle real-life situations safely.
            </p>
            </Card>
          </Container>
      </section>

      <section className="bg-grey-300 py-12">
        <Container additionalClassNames="mb-6">

          <div className='flex flex-col md:flex-row gap-8'>
            <div className="h4 flex items-center mb-8">
              <span className="text-2xl flex items-center justify-center w-24 h-24 bg-green-500 rounded-circle border-5 border-white color-white mr-2">
                {completedLessonsCount}
                {` `}
                <span className="text-sm weight-500 block mx-1">of</span>
                {` `}
                {lessonsExludingBackstory.length}
              </span>
              <span className="text-uppercase">lessons complete</span>
            </div>

            <div className="h4 flex items-center mb-8">
              <span className="text-2xl flex items-center justify-center w-24 h-24 bg-green-500 rounded-circle border-5 border-white color-white mr-2">
                {feedbackSteps.filter(({ isComplete }) => isComplete).length}
                {` `}
                <span className="text-sm weight-500 block mx-1">of</span>
                {` `}
                {feedbackSteps.length}
              </span>
              <span className="text-uppercase">Your feedback</span>
            </div>
          </div>



          <Card additionalClassNames="mb-12">
            <h3 className="h5 mb-4 text-uppercase">Useful links</h3>
            <div className="flex flex-wrap">
              {isLoadingAssets ? (
                <SkeletonLoader additionalClassNames='h-12 w-full' />
              ) : (
                <>
                  {characterPoster && (
                    <Button
                      additionalClassNames="mb-3 md:mb-0 mr-2"
                      linkButton={{ id: characterPoster?.assetId, url: String(characterPoster?.presignedUrl), target: '_blank' }}
                      text="Characters (A3 poster)"
                      variant="dark"
                    />
                  )}

                  {standardDrinkChart && (
                    <Button
                      additionalClassNames="mb-3 md:mb-0 mr-2"
                      linkButton={{ id: standardDrinkChart?.assetId, url: String(standardDrinkChart?.presignedUrl), target: '_blank' }}
                      text="Standard drink chart"
                      variant="dark"
                    />
                  )}

                  {childDietary && (
                    <Button
                      additionalClassNames="mb-3 md:mb-0 mr-2"
                      linkButton={{ id: childDietary?.assetId, url: String(childDietary?.presignedUrl), target: '_blank' }}
                      text="Healthy Eating Guide"
                      variant="dark"
                    />
                  )}
                </>
              )}
            </div>
          </Card>

          <LessonCards completedFeedbackCount={feedbackSteps.filter(({ isComplete }) => isComplete).length} lessons={lessons} isLoading={isLessonsLoading} roleTag="Student" />
        </Container>
      </section>

    </Page>
  );
};
